import { Autocomplete, Chip, TextField } from "@mui/material";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

type Props<FormType extends FieldValues> = {
  control: Control<FormType>;
  name: FieldPath<FormType>;
  label: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
};

/**
 * A list of free-text values (URLs, path prefixes) entered one by one: Enter adds, the chip's cross removes.
 * The field value is an array of strings; validation messages come from the array as a whole.
 */
export const StringListInput = <FormType extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  helperText,
  disabled,
}: Props<FormType>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => {
      const values: Array<string> = Array.isArray(field.value)
        ? field.value
        : [];
      const hasError = fieldState.error !== undefined;

      return (
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={values}
          disabled={disabled}
          onChange={(_, value) =>
            field.onChange(value.map((item) => item.trim()).filter(Boolean))
          }
          onBlur={field.onBlur}
          renderValue={(items, getItemProps) =>
            items.map((item, index) => {
              const { key, ...itemProps } = getItemProps({ index });
              return (
                <Chip key={key} label={item} size="small" {...itemProps} />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={hasError}
              helperText={hasError ? fieldState.error?.message : helperText}
            />
          )}
        />
      );
    }}
  />
);
